import { useLiveRefresh } from "../utils/live";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import Button from "../components/common/Button";
import Spinner from "../components/common/Spinner";
import Modal from "../components/common/Modal";
import QuoteBuilder from "../components/admin/QuoteBuilder";
import RainOutModal from "../components/admin/RainOutModal";
import { listQuotes, deleteQuote, sendQuoteToCustomer, openQuotePdf, undoRainOut } from "../api/quotes";
import { formatUSD } from "../utils/money";
import { formatEasternDate, formatEasternTime } from "../utils/dates";
import { notifyCountsChanged } from "../utils/navCounts";
import { viewSummary } from "../utils/contact";
import {
  STAGE,
  STAGE_META,
  stageOf,
  isPaid,
  isAwaitingPayment,
  isUpcoming,
  amountPaidCents,
  balanceCents,
  payPageUrl,
  sortByEventDate,
} from "../utils/jobs";

// One list for every job, filtered by what you're looking for. Replaces the
// old split between the Pipeline board and the flat Quotes list.
const FILTERS = [
  { key: "active", label: "Active" },
  { key: "awaiting", label: "Awaiting payment" },
  { key: "upcoming", label: "Upcoming" },
  { key: "drafts", label: "Drafts" },
  { key: "paid", label: "Paid" },
  { key: "all", label: "All" },
];

function matches(job, filter, now) {
  const stage = stageOf(job, now);
  switch (filter) {
    case "awaiting":
      return isAwaitingPayment(job, now);
    case "upcoming":
      return isUpcoming(job, 30, now) && stage !== STAGE.LOST;
    case "drafts":
      return stage === STAGE.DRAFT;
    case "paid":
      return (
        stage === STAGE.DEPOSIT || stage === STAGE.PAID || stage === STAGE.COMPLETED
      );
    case "all":
      return true;
    case "active":
    default:
      return stage !== STAGE.COMPLETED && stage !== STAGE.LOST;
  }
}

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("active");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [rainJob, setRainJob] = useState(null);
  const [params, setParams] = useSearchParams();

  // ?open=<id> (or ?open=new) deep-links straight into the builder.
  const openParam = params.get("open");
  const [builder, setBuilder] = useState(null); // {quoteId} | null

  // `quiet`: background refreshes keep the list on screen instead of a spinner.
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError("");
    try {
      setJobs(await listQuotes({}));
    } catch (e) {
      setError(e.message || "Could not load jobs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  const quietLoad = useCallback(() => load({ quiet: true }), [load]);
  useLiveRefresh(quietLoad);

  useEffect(() => {
    if (!openParam) return;
    setBuilder({ quoteId: openParam === "new" ? null : Number(openParam) });
    // Clear the param so closing the modal doesn't immediately reopen it.
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("open");
        return next;
      },
      { replace: true }
    );
  }, [openParam, setParams]);

  const visible = useMemo(() => {
    const now = Date.now();
    return jobs.filter((j) => matches(j, filter, now)).sort(sortByEventDate);
  }, [jobs, filter]);

  async function handleSend(job) {
    setBusyId(job.id);
    setError("");
    try {
      await sendQuoteToCustomer(job.id);
      await load();
      notifyCountsChanged();
    } catch (e) {
      setError(e.message || "Could not send the quote.");
    } finally {
      setBusyId(null);
    }
  }

  async function handlePdf(job) {
    setBusyId(job.id);
    try {
      await openQuotePdf(job.id);
    } catch (e) {
      setError(e.message || "Could not open the PDF.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleUndoRain(job) {
    if (!window.confirm(`Undo the rain-out on ${job.quote_number}? Its credit is removed.`)) return;
    setBusyId(job.id);
    setError("");
    try {
      await undoRainOut(job.id);
      await load();
      notifyCountsChanged();
    } catch (e) {
      setError(e.message || "Could not undo the rain-out.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete() {
    const job = confirmDelete;
    if (!job) return;
    setBusyId(job.id);
    try {
      await deleteQuote(job.id);
      setConfirmDelete(null);
      await load();
      notifyCountsChanged();
    } catch (e) {
      setError(e.message || "Could not delete the quote.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <AppShell
      title="Jobs"
      action={
        <Button onClick={() => setBuilder({ quoteId: null })} className="py-2 shrink-0">
          + New
        </Button>
      }
    >
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition ${
              filter === f.key
                ? "bg-birdie-green text-white"
                : "bg-white text-birdie-charcoal/65 border border-birdie-green/15"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-16 flex justify-center">
          <Spinner label="Loading jobs..." />
        </div>
      ) : visible.length === 0 ? (
        <div className="mt-4 bg-white border border-birdie-green/15 rounded-2xl p-10 text-center">
          <div className="text-3xl mb-2">🗂️</div>
          <p className="text-sm text-birdie-charcoal/60">Nothing here yet.</p>
        </div>
      ) : (
        <div className="mt-3 space-y-2.5">
          {visible.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              busy={busyId === job.id}
              onOpen={() => setBuilder({ quoteId: job.id })}
              onSend={() => handleSend(job)}
              onPdf={() => handlePdf(job)}
              onDelete={() => setConfirmDelete(job)}
              onRainOut={() => setRainJob(job)}
              onUndoRain={() => handleUndoRain(job)}
            />
          ))}
        </div>
      )}

      {builder && (
        <QuoteBuilder
          quoteId={builder.quoteId}
          onClose={(changed) => {
            setBuilder(null);
            if (changed) load().then(notifyCountsChanged);
          }}
        />
      )}

      {rainJob && (
        <RainOutModal
          job={rainJob}
          onClose={() => setRainJob(null)}
          onDone={() => {
            setRainJob(null);
            load().then(notifyCountsChanged);
          }}
        />
      )}

      <Modal
        isOpen={Boolean(confirmDelete)}
        title="Delete quote"
        onClose={() => setConfirmDelete(null)}
        maxWidthClass="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-birdie-charcoal">
            Delete <strong>{confirmDelete?.quote_number}</strong> for{" "}
            {confirmDelete?.customer_name}? This also removes its calendar hold and can't be undone.
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setConfirmDelete(null)} className="flex-1">
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={busyId !== null} className="flex-1">
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}

function JobCard({ job, busy, onOpen, onSend, onPdf, onDelete, onRainOut, onUndoRain }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const stage = stageOf(job);
  // A rained-out job has no balance left, which would otherwise read "Paid in full".
  const meta = job.rained_out_at
    ? { label: "Rained out", cls: "bg-sky-100 text-sky-800" }
    : STAGE_META[stage];
  const url = payPageUrl(job);
  // Money has changed hands — don't offer Send/Delete on these.
  const locked =
    stage === STAGE.DEPOSIT || stage === STAGE.PAID || stage === STAGE.COMPLETED;
  const owed = balanceCents(job);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <div className="bg-white border border-birdie-green/15 rounded-2xl p-3.5">
      <button onClick={onOpen} className="w-full text-left">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`}>
                {meta.label}
              </span>
              <span className="font-mono text-[10px] text-birdie-charcoal/40">
                {job.quote_number}
              </span>
            </div>
            <div className="font-semibold text-birdie-charcoal truncate mt-1">
              {job.customer_name}
            </div>
            <div className="text-xs text-birdie-charcoal/60 truncate">
              {job.title || "Untitled job"}
            </div>
            {job.event_date && (
              <div className="text-xs text-birdie-charcoal/50 mt-1">
                📅 {formatEasternDate(job.event_date)} · {formatEasternTime(job.event_date)}
              </div>
            )}
            {isPaid(job) && owed > 0 && (
              <div className="text-xs font-semibold text-blue-700 mt-1">
                {formatUSD(amountPaidCents(job))} paid · {formatUSD(owed)} still due
              </div>
            )}
            {!locked && viewSummary(job) && (
              <div className={`text-xs mt-1 ${job.view_count ? "text-birdie-green font-semibold" : "text-birdie-charcoal/45"}`}>
                {job.view_count ? "👀 " : ""}
                {viewSummary(job)}
              </div>
            )}
            {job.rained_out_at && (
              <div className="text-xs font-semibold text-sky-800 mt-1">
                🌧 Rained out after {Number(job.rain_hours_played || 0)}h
                {job.rain_credit_cents > 0 && ` · ${formatUSD(job.rain_credit_cents)} credit`}
              </div>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="font-bold text-birdie-green text-lg">{formatUSD(job.total_cents)}</div>
            {job.deposit_cents > 0 && (
              <div className="text-[11px] text-birdie-charcoal/50">
                {formatUSD(job.deposit_cents)} deposit
              </div>
            )}
          </div>
        </div>
      </button>

      <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-birdie-green/10">
        <Act onClick={onOpen} disabled={busy}>Open</Act>
        <Act onClick={() => navigate(`/admin/customers/${job.customer_id}`)}>Customer</Act>
        <Act onClick={onPdf} disabled={busy}>PDF</Act>
        {url && !job.rained_out_at && (
          <Act onClick={copy} disabled={busy}>{copied ? "Copied!" : "Pay link"}</Act>
        )}
        {locked && !job.rained_out_at && (
          <Act onClick={onRainOut} disabled={busy}>Rained out</Act>
        )}
        {job.rained_out_at && (
          <Act onClick={onUndoRain} disabled={busy}>Undo rain-out</Act>
        )}
        {!locked && (
          <Act onClick={onSend} disabled={busy} primary>
            {busy ? "…" : job.status === "sent" ? "Re-send" : "Send"}
          </Act>
        )}
        {!locked && (
          <Act onClick={onDelete} disabled={busy} danger>
            Delete
          </Act>
        )}
      </div>
    </div>
  );
}

function Act({ children, onClick, disabled, primary, danger }) {
  const base =
    "px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-40 whitespace-nowrap";
  const style = primary
    ? "bg-birdie-green text-white hover:bg-birdie-green-dark"
    : danger
    ? "text-red-600 hover:bg-red-50"
    : "border border-birdie-green/20 text-birdie-charcoal/75 hover:bg-birdie-cream";
  return (
    <button onClick={onClick} disabled={disabled} className={`${base} ${style}`}>
      {children}
    </button>
  );
}
